import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, Heading, Paragraph } from '@digdir/designsystemet-react';
import type { MeasureStep } from '../../../api/workEnvironmentTypes';
import {
  useCloseHighlightsMutation,
  useCloseProposalsMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { closeButtonVariant, closeEarlyText, closeErrorKey } from '../measuresView';
import { dayAndMonth } from '../surveyProcessView';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; step: MeasureStep; deadline: string | null };

/** What comes after the step, a way to end it before the deadline, and to start the highlighting after the review */
export const MeasuresNextCard = ({ surveyId, step, deadline }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  // The step changes as soon as it is closed, so the dialog belongs to the step it was opened for
  const [confirming, setConfirming] = useState<MeasureStep | null>(null);
  const closeProposals = useCloseProposalsMutation(surveyId);
  const closeHighlights = useCloseHighlightsMutation(surveyId);
  const close = step === 'highlight' ? closeHighlights : closeProposals;
  const date = deadline ? dayAndMonth(deadline, i18n.language) : null;
  const early = step === 'plan' ? null : closeEarlyText(step, deadline, t, i18n.language);

  return (
    <section aria-labelledby="measures-next-heading" className={`${process.card} ${process.sideCard}`} data-gap="sm">
      <span className={`ds-label ${process.eyebrow}`} data-size="sm">
        {t('workEnvironmentProcess.next.label')}
      </span>
      <Heading level={2} data-size="xs" id="measures-next-heading">
        {t(`workEnvironmentMeasures.next.${step}.title`)}
      </Heading>
      <Paragraph data-size="sm" className={process.subtle}>
        {date
          ? t(`workEnvironmentMeasures.next.${step}.text`, { date })
          : t(`workEnvironmentMeasures.next.${step}.textWithoutDeadline`)}
      </Paragraph>
      {step !== 'plan' && may('advance') && (
        <div>
          <Button variant={closeButtonVariant(step)} data-size="sm" onClick={() => setConfirming(step)}>
            {t(`workEnvironmentMeasures.next.${step}.close`)}
          </Button>
        </div>
      )}
      {confirming === step && step !== 'plan' && (
        <Dialog
          open
          onClose={() => setConfirming(null)}
          closedby="any"
          closeButton={t('workEnvironmentRisk.data.close')}
        >
          <div className={classes.areaBody}>
            <Heading level={2} data-size="xs">
              {t(`workEnvironmentMeasures.next.${step}.confirmTitle`)}
            </Heading>
            <Paragraph>{t(`workEnvironmentMeasures.next.${step}.confirmText`)}</Paragraph>
            {early && <Paragraph>{early}</Paragraph>}
            {close.isError && (
              <Alert data-color="danger">
                <Paragraph>{t(`workEnvironmentMeasures.next.${closeErrorKey(close.error)}`)}</Paragraph>
              </Alert>
            )}
            <div className={classes.dialogButtons}>
              <Button
                variant="primary"
                data-size="sm"
                loading={close.isPending}
                onClick={() => close.mutate(undefined, { onSuccess: () => setConfirming(null) })}
              >
                {t(`workEnvironmentMeasures.next.${step}.close`)}
              </Button>
              <Button variant="tertiary" data-size="sm" onClick={() => setConfirming(null)}>
                {t('workEnvironmentRisk.assessment.cancel')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
};
